import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { cn } from "design-system/utils"
import {
  buildWebhookHeaders,
  buildWebhookTestContext,
  parseWebhookPriorResponseBody,
  renderWebhookBody,
  WEBHOOK_BODY_CONTENT_TYPE,
  WebhookBodyFormat,
} from "lopermut"
import { Check, FlaskConical } from "lucide-react"
import { useTestWebhookChainStepMutation } from "modules/capabilities/hooks/useTestWebhookChainStepMutation"
import { useTestWebhookMutation } from "modules/capabilities/hooks/useTestWebhookMutation"
import { Loading } from "modules/shared/components/Loading"
import { useVaultPreviewContext } from "modules/shared/hooks/useVaultPreviewContext"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { TestWebhookResponseDataSchema, WebhookCapabilityConfigSchema, WebhookMethod } from "schema"
import { toast } from "sonner"
import z from "zod"

type WebhookTestFormValues = Pick<
  z.infer<typeof WebhookCapabilityConfigSchema>,
  "url" | "method" | "webhookHeaders" | "webhookBody" | "webhookBodyFormat"
>

type Props = {
  formValues: WebhookTestFormValues
  disabled: boolean
  onSuccess: (webhookResponse: string) => void
  onError: (error: unknown) => void
  priorResponses?: string[]
}

const CHAIN_STEP_VARIABLE_PATTERN = /^steps\[\d+\]/

const PREVIEW_MARKDOWN_PROPS = {
  className: "min-w-0 max-w-full overflow-auto",
  cardClassName: "border-none shadow-none min-w-0 max-w-full",
  codeClassName: "border-none whitespace-pre-wrap break-all",
  shortenTextAfterLength: 0,
} as const

type TestResult = z.infer<typeof TestWebhookResponseDataSchema> | null

function extractVariables(text: string): string[] {
  const variablePattern = /\{\{([^}]+)\}\}/g
  const variables: string[] = []
  let match
  while ((match = variablePattern.exec(text)) !== null) {
    const variableName = match[1]?.trim()
    if (variableName && !variableName.startsWith("vault.") && !variables.includes(variableName)) {
      variables.push(variableName)
    }
  }
  return variables
}

function replaceVariables(text: string, values: Record<string, string>): string {
  return text.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
    const trimmedName = variableName.trim()
    return values[trimmedName] !== undefined ? values[trimmedName] : match
  })
}

function getVariableFromPriorResponses(variable: string, priorResponses: string[]) {
  const match = /^steps\[(\d+)]\.(.+)$/.exec(variable)
  if (!match) return ""

  const stepIndex = Number(match[1])
  const key = match[2]!
  const step = parseWebhookPriorResponseBody(priorResponses[stepIndex] ?? "")
  const value = step[key]

  return value === null || value === undefined ? "" : String(value)
}

export function TestWebhookDialog({
  formValues,
  disabled,
  onSuccess,
  onError,
  priorResponses,
}: Props) {
  const { t } = useTranslation()
  const isChainStep = priorResponses !== undefined
  const [open, setOpen] = useState(false)
  const [testResult, setTestResult] = useState<TestResult>(null)
  const [responseSaved, setResponseSaved] = useState(false)
  const [variableValues, setVariableValues] = useState<Record<string, string>>({})
  const testContext = useMemo(
    () => buildWebhookTestContext(variableValues, priorResponses),
    [variableValues, priorResponses],
  )
  const previewContext = useVaultPreviewContext(testContext)

  const { method } = formValues
  const url = formValues.url ?? ""

  const modifiedUrl = method === WebhookMethod.GET ? replaceVariables(url, variableValues) : url

  const handleSuccess = (responseBody: string) => {
    setTestResult({ valid: true, responseBody: responseBody ?? null, error: undefined })
    setResponseSaved(false)
  }

  const handleSaveResponse = () => {
    if (testResult?.valid && testResult.responseBody) {
      onSuccess(testResult.responseBody)
      setResponseSaved(true)
      toast.success(t("capabilities.webhookConfig.testWebhook.responseSaved"))
      setOpen(false)
    }
  }

  const handleError = (error: unknown) => {
    const errorMessage = error instanceof Error ? error.message : String(error)
    setTestResult({ valid: false, responseBody: null, error: errorMessage })
    onError(error)
  }

  const { mutateAsync: testWebhook, isPending: isTestWebhookPending } = useTestWebhookMutation({
    onSuccess: handleSuccess,
    onError: handleError,
  })

  const { mutateAsync: testWebhookChainStep, isPending: isTestChainStepPending } =
    useTestWebhookChainStepMutation({
      onSuccess: handleSuccess,
      onError: handleError,
    })

  const isTestPending = isChainStep ? isTestChainStepPending : isTestWebhookPending
  const testButtonLabel = isChainStep
    ? t("capabilities.webhookConfig.chain.testStep")
    : t("capabilities.webhookConfig.testWebhook.button")

  const allVariables = useMemo(() => {
    const variables = new Set<string>()

    if (formValues.method === WebhookMethod.GET) {
      extractVariables(formValues.url ?? "").forEach((v) => variables.add(v))
    }

    if (formValues.webhookBody) {
      extractVariables(formValues.webhookBody).forEach((v) => variables.add(v))
    }

    for (const header of formValues.webhookHeaders || []) {
      if (header.value) {
        extractVariables(header.value).forEach((v) => variables.add(v))
      }
    }

    return Array.from(variables).sort()
  }, [formValues.method, formValues.webhookBody, formValues.url, formValues.webhookHeaders])

  const priorResponsesKey = priorResponses?.join("\0") ?? ""

  useEffect(() => {
    if (!open) return
    setTestResult(null)
    setResponseSaved(false)
  }, [open])

  useEffect(() => {
    if (!open) return
    setVariableValues((prev) => {
      const initialValues: Record<string, string> = {}
      for (const variable of allVariables) {
        const priorDefault =
          priorResponses?.length && CHAIN_STEP_VARIABLE_PATTERN.test(variable)
            ? getVariableFromPriorResponses(variable, priorResponses)
            : ""
        initialValues[variable] = prev[variable] || priorDefault || ""
      }
      return initialValues
    })
    // priorResponses content is tracked via priorResponsesKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allVariables, priorResponsesKey])

  const handleTest = async () => {
    const {
      webhookHeaders,
      webhookBody,
      webhookBodyFormat = WebhookBodyFormat.json,
      method,
      url,
    } = formValues
    if (isChainStep) {
      try {
        const result = await testWebhookChainStep({
          step: {
            url: url ?? "",
            method,
            webhookHeaders: webhookHeaders ?? [],
            webhookBody: webhookBody ?? "{}",
            webhookBodyFormat,
          },
          priorResponses,
          variables: variableValues,
        })

        if (!result.valid) {
          handleError(result.error ?? t("capabilities.webhookConfig.testWebhook.error"))
        }
      } catch (error) {
        handleError(error)
      }
      return
    }

    try {
      const result = await testWebhook({
        url: url ?? "",
        webhookHeaders: webhookHeaders ?? [],
        webhookBody: webhookBody ?? "{}",
        webhookBodyFormat,
        method,
        variables: variableValues,
      })

      if (!result.valid) {
        handleError(result.error ?? t("capabilities.webhookConfig.testWebhook.error"))
      }
    } catch (error) {
      handleError(error)
    }
  }

  const isUrlValid = useMemo(() => {
    const rawUrl = formValues.url ?? ""
    if (!rawUrl.trim()) return false

    if (isChainStep) {
      if (formValues.method === WebhookMethod.GET && rawUrl.includes("{{")) return true
      return z.url().safeParse(rawUrl).success
    }

    if (formValues.method === WebhookMethod.GET && rawUrl.includes("{{")) return true

    return z.url().safeParse(modifiedUrl).success
  }, [formValues.url, formValues.method, modifiedUrl, isChainStep])
  const areAllVariablesFilled = useMemo(() => {
    if (allVariables.length === 0) return true
    return allVariables.every((variable) => {
      const value = variableValues[variable]
      return value !== undefined && value.trim() !== ""
    })
  }, [allVariables, variableValues])

  const requestPreview = useMemo(() => {
    const method = formValues.method || "GET"
    const webhookBodyFormat = formValues.webhookBodyFormat ?? WebhookBodyFormat.json
    const context = previewContext
    let bodyString: string | null = null
    let bodyError: string | undefined
    let contentType = WEBHOOK_BODY_CONTENT_TYPE[webhookBodyFormat]

    if (method !== "GET") {
      try {
        const result = renderWebhookBody(formValues.webhookBody || "{}", webhookBodyFormat, context)
        bodyString = result.bodyString ?? null
        contentType = result.contentType
      } catch (error) {
        bodyError = error instanceof Error ? error.message : String(error)
      }
    }

    const headers = buildWebhookHeaders(formValues.webhookHeaders || [], contentType, context)

    return {
      method,
      url: modifiedUrl || "",
      headers,
      body: bodyString,
      bodyError,
      bodyLanguage: webhookBodyFormat === WebhookBodyFormat.json ? "json" : "text",
    }
  }, [
    formValues.method,
    formValues.webhookBodyFormat,
    modifiedUrl,
    formValues.webhookBody,
    formValues.webhookHeaders,
    previewContext,
  ])

  const canTest =
    !disabled && isUrlValid && !isTestPending && areAllVariablesFilled && !requestPreview.bodyError

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full" disabled={disabled}>
          <FlaskConical />
          {testButtonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent size="2xl" className="min-w-0">
        <DialogHeader className="min-w-0">
          <DialogTitle>{t("capabilities.webhookConfig.testWebhook.title")}</DialogTitle>
          <DialogDescription className="break-words">
            {t("capabilities.webhookConfig.testWebhook.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-w-0 flex-col gap-4">
          {allVariables.length > 0 && (
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium">
                {t("capabilities.webhookConfig.testWebhook.variables.title")}
              </p>
              <div className="grid sm:grid-cols-2 grid-cols-1 gap-4 border rounded-lg p-4 bg-secondary">
                {allVariables.map((variable) => (
                  <div key={variable} className="flex flex-col gap-1">
                    <Label htmlFor={`variable-${variable}`} className="text-xs">
                      {variable}
                    </Label>
                    <Input
                      id={`variable-${variable}`}
                      type="text"
                      value={variableValues[variable] || ""}
                      placeholder={t(
                        "capabilities.webhookConfig.testWebhook.variables.placeholder",
                      )}
                      required
                      onChange={(e) =>
                        setVariableValues((prev) => ({ ...prev, [variable]: e.target.value }))
                      }
                    />
                  </div>
                ))}
              </div>
              {!areAllVariablesFilled && (
                <p className="text-sm text-muted-foreground">
                  {t("capabilities.webhookConfig.testWebhook.variables.required")}
                </p>
              )}
            </div>
          )}
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium">
              {t("capabilities.webhookConfig.testWebhook.preview.title")}
            </p>
            <div className="flex min-w-0 flex-col gap-2 border rounded-lg p-4 bg-secondary">
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-muted-foreground">
                  {t("capabilities.webhookConfig.testWebhook.preview.method")}
                </p>
                <p className="text-sm font-mono">{requestPreview.method}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-muted-foreground">
                  {t("capabilities.webhookConfig.testWebhook.preview.url")}
                </p>
                <p className="text-sm font-mono break-all">{requestPreview.url || "-"}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-muted-foreground">
                  {t("capabilities.webhookConfig.testWebhook.preview.headers")}
                </p>
                <MarkdownRenderer {...PREVIEW_MARKDOWN_PROPS}>
                  {`\`\`\`json\n${JSON.stringify(requestPreview.headers, null, 2)}\n\`\`\``}
                </MarkdownRenderer>
              </div>
              {requestPreview.method !== "GET" && (
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t("capabilities.webhookConfig.testWebhook.preview.body")}
                  </p>
                  {requestPreview.bodyError && (
                    <p className="text-sm font-mono text-destructive break-all">
                      {requestPreview.bodyError}
                    </p>
                  )}
                  {!requestPreview.bodyError && requestPreview.body && (
                    <MarkdownRenderer
                      {...PREVIEW_MARKDOWN_PROPS}
                      className={cn(PREVIEW_MARKDOWN_PROPS.className, "max-h-[200px]")}
                    >
                      {`\`\`\`${requestPreview.bodyLanguage}\n${requestPreview.body}\n\`\`\``}
                    </MarkdownRenderer>
                  )}
                  {!requestPreview.bodyError && !requestPreview.body && (
                    <p className="text-sm font-mono text-muted-foreground">{"-"}</p>
                  )}
                </div>
              )}
            </div>
          </div>
          <Button onClick={handleTest} disabled={!canTest}>
            {isTestPending ? t("capabilities.webhookConfig.testWebhook.testing") : testButtonLabel}
          </Button>
          {isTestPending && <Loading minimal />}
          {!isTestPending && testResult && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Badge variant={testResult.valid ? "success" : "destructive"}>
                  {testResult.valid
                    ? t("capabilities.webhookConfig.testWebhook.success")
                    : t("capabilities.webhookConfig.testWebhook.error")}
                </Badge>
              </div>
              {testResult.valid && testResult.responseBody && (
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium">{t("capabilities.executions.result")}</p>
                  <MarkdownRenderer
                    {...PREVIEW_MARKDOWN_PROPS}
                    className={cn(PREVIEW_MARKDOWN_PROPS.className, "max-h-[300px]")}
                  >
                    {`\`\`\`json\n${testResult.responseBody}\n\`\`\``}
                  </MarkdownRenderer>
                </div>
              )}
              {!testResult.valid && testResult.error && (
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-destructive">
                    {t("capabilities.executions.errorMessage")}
                  </p>
                  <MarkdownRenderer
                    {...PREVIEW_MARKDOWN_PROPS}
                    className={cn(PREVIEW_MARKDOWN_PROPS.className, "max-h-[300px]")}
                  >
                    {`\`\`\`json\n${testResult.error}\n\`\`\``}
                  </MarkdownRenderer>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("shared.close")}
          </Button>
          <Button
            onClick={handleSaveResponse}
            disabled={!testResult?.valid || !testResult?.responseBody || responseSaved}
          >
            {responseSaved ? (
              <>
                <Check className="size-4" />
                {t("capabilities.webhookConfig.testWebhook.saveResponse")}
              </>
            ) : (
              t("capabilities.webhookConfig.testWebhook.saveResponse")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
